import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const AddWaterBalance = () => {
  const { accessToken } = useAuth();
  const baseURL = process.env.REACT_APP_BASEURL;
  const navigate = useNavigate();

  const [sites, setSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(true);

  const [formData, setFormData] = useState({
    site_id: "",
    water_balance: "",
    month: "",
    unit: "litres",
  });

  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState({
    open: false,
    type: "",
    message: "",
  });

  // Fetch sites for dropdown (same as Dashboard)
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const res = await axios.get(`${baseURL}/get-sites`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        setSites(res.data.data || []);
      } catch (err) {
        console.error("Failed to load sites", err);
      } finally {
        setLoadingSites(false);
      }
    };

    fetchSites();
  }, [accessToken, baseURL]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(
        `${baseURL}/water-balance`,
        {
          site_id: formData.site_id,
          water_balance: Number(formData.water_balance),
          month: formData.month,
          unit: formData.unit,
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      setFeedback({
        open: true,
        type: "success",
        message: "Water balance added successfully!",
      });

      // Redirect after slight delay (optional)
      setTimeout(() => {
        navigate("/water-balance");
      }, 800);

      setFormData({
        site_id: "",
        water_balance: "",
        month: "",
        unit: "litres",
      });
    } catch (error) {
      console.error(error);
      setFeedback({
        open: true,
        type: "error",
        message: error.response?.data?.error || "Failed to add water balance",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card sx={{ maxWidth: 600, mx: "auto", mt: 4, p: 2 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Add Water Balance
        </Typography>

        <form onSubmit={handleSubmit}>
          {/* SITE SELECT DROPDOWN */}
          <FormControl fullWidth margin="normal" required>
            <InputLabel id="site-select-label">Select Site</InputLabel>
            <Select
              labelId="site-select-label"
              name="site_id"
              value={formData.site_id}
              label="Select Site"
              onChange={handleChange}
              disabled={loadingSites}
            >
              {sites.map((site) => (
                <MenuItem key={site.id} value={site.id}>
                  {site.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Water Balance (Litres)"
            name="water_balance"
            type="number"
            value={formData.water_balance}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
          />

          <TextField
            label="Month"
            name="month"
            type="month"
            value={formData.month}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Unit"
            name="unit"
            value={formData.unit}
            onChange={handleChange}
            fullWidth
            margin="normal"
            disabled
          />

          <Box sx={{ textAlign: "right", mt: 2, display: "flex", justifyContent: "flex-end", gap: "1rem" }}>
            <Button variant="contained" color="secondary" onClick={() => navigate(-1)}>
              Back
            </Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : "Add Record"}
            </Button>
          </Box>
        </form>
      </CardContent>

      <Snackbar
        open={feedback.open}
        autoHideDuration={3000}
        onClose={() => setFeedback({ ...feedback, open: false })}
      >
        <Alert severity={feedback.type}>{feedback.message}</Alert>
      </Snackbar>
    </Card>
  );
};

export default AddWaterBalance;
